import { ReactElement, useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PAGINATION_SIZES, PAGINATION_SIZES_25 } from './table.const';

import {
  RfCdkTable,
  RfUiCardTable,
  RfUiPagination,
  RfUiSelectList,
} from 'renfe-components';
import { createStructuredSelector } from 'reselect';
import {
  makeSelectElementsPerPage,
  makeSelectInfoUser,
  makeSelectPages,
  makeSelectPracticas,
  makeSelectPracticasAuditoriaCount,
  makeSelectPracticasAuditoriaParse,
  makeSelectPracticaSeleccionada,
  makeSelectPracticasList,
  makeSelectPracticasListCount,
  makeSelectPracticesParse,
  makeSelectSelectedPage,
  makeSelectTodasPracticasAuditoria,
} from 'store/practicas/selectors';
import {
  setElementsPerPage,
  setSelectedPage,
  setSort,
} from 'store/practicas/actions';
import {
  setPracticaAuditoriaSeleccionada,
  setPracticaRechazadaSeleccionada,
  setPracticaReclamadaSeleccionada,
  setPracticaValidadaSeleccionada,
} from 'store/app/actions';
import { LiteralHeaderTable, Sort } from 'types';
import {
  makeSelectInstructorsSelected,
  makeSelectObservacionesInstructor,
  makeSelectPracticaAuditoriaSeleccionada,
  makeSelectPracticaRechazadaSeleccionada,
  makeSelectPracticaReclamadaSeleccionada,
  makeSelectPracticesValidSelect,
} from 'store/app/selectors';
import { getSortLabel } from 'utils/sortUtils';
import {
  makeSelectIsAdminGestorProfile,
  makeSelectIsFormadorProfile,
  makeSelectIsInstructorProfile,
  makeSelectIsManagerProfile,
  makeSelectPerfilesUsuarioDatosPerfilesUsuario,
  makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected,
} from 'store/usuarios/selectors';
import { getHeaderAudit } from './utils';
import './Table.scss';
import {
  instructorHoursHeaders,
  parseArrayData,
  parseReportHoursData,
} from './table-utils';
import { isMobileOnly } from 'react-device-detect';
import {
  paginationChangeNumber,
} from 'utils/practiceUtils';

const selectors = createStructuredSelector({
  practices: makeSelectPracticas(),
  practicas: makeSelectPracticasList(),
  practicesCount: makeSelectPracticasListCount(),
  practicesAuditoriaCount: makeSelectPracticasAuditoriaCount(),
  practicaSeleccionada: makeSelectPracticaSeleccionada(),
  practicesParse: makeSelectPracticesParse(),
  pages: makeSelectPages(),
  selectedPage: makeSelectSelectedPage(),
  elementsPerPage: makeSelectElementsPerPage(),
  todasPracticasAuditoria: makeSelectTodasPracticasAuditoria(),
  practicasAuditoriaParse: makeSelectPracticasAuditoriaParse(),
  practicasAuditoriaSeleccionadas: makeSelectPracticaAuditoriaSeleccionada(),
  practicasReclamadaSeleccionadas: makeSelectPracticaReclamadaSeleccionada(),
  practicasRechazadaSeleccionadas: makeSelectPracticaRechazadaSeleccionada(),
  practicesValidSelect: makeSelectPracticesValidSelect(),
  observaciones_instructor: makeSelectObservacionesInstructor(),
  profileActive: makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected(),
  infoUser: makeSelectInfoUser(),
  perfilesUsuario: makeSelectPerfilesUsuarioDatosPerfilesUsuario(),
  selectedInstructors: makeSelectInstructorsSelected(),
  isInstructorProfile: makeSelectIsInstructorProfile(),
  isManagerProfile: makeSelectIsManagerProfile(),
  isFormadorProfile: makeSelectIsFormadorProfile(),
  isAdminGestorProfile: makeSelectIsAdminGestorProfile()
} as any);

const getDefaultPagination = (pagination25: boolean): string =>
  pagination25 ? '25' : '10';

const getPaginationSizes = (pagination25: boolean): any[] =>
  pagination25 ? PAGINATION_SIZES_25 : PAGINATION_SIZES;

const getSessionNumber = (key: string, defaultValue: string): number =>
  parseInt(sessionStorage.getItem(key) ?? defaultValue);

const getVisiblePages = (): string =>
  window.innerWidth <= 1023 ? '3' : '5';

const hasData = (dataCount: number): boolean => dataCount !== 0;

const isJsonArrayEmpty = (arrayData: string): boolean =>
  !JSON.parse(arrayData).length;

const shouldShowPaginator = (
  arrayDataIsEmpty: boolean,
  pages: number,
  handlePage: number
): boolean => !arrayDataIsEmpty && (pages > 1 || handlePage > 1);

const shouldShowStorePagination = (
  handleFilter: any,
  pages: number
): boolean => !handleFilter && Number.isInteger(pages);

const getActiveElementsPerPage = (
  pagination25: boolean
): number => {
  const storedValue = sessionStorage.getItem(
    `elementsPerPage-${window.location.pathname}`
  );

  if (storedValue !== null) {
    return Number(storedValue);
  }

  return pagination25 ? 25 : 10;
};

const buildPaginationOptions = (
  pagination25: boolean
): string => {
  const activeElementsPerPage = getActiveElementsPerPage(pagination25);

  return JSON.stringify(
    [...getPaginationSizes(pagination25)].map((epp) => ({
      ...epp,
      active: epp.value === activeElementsPerPage ? 'true' : 'false',
    }))
  );
};

const buildArrayData = (
  data: any,
  list: boolean,
  practicesParse: string,
  practicasAuditoriaParse: string
): string => JSON.stringify(data) || (list ? practicesParse : practicasAuditoriaParse);

const getHeaderTable = (
  isReportHours: boolean,
  headers: LiteralHeaderTable[] | undefined,
  literalsHeaderTable: LiteralHeaderTable[]
): string => {
  const headerData = headers || literalsHeaderTable;

  if (isReportHours) {
    return JSON.stringify(instructorHoursHeaders(headerData));
  }

  return JSON.stringify(headerData);
};

const getBodyTable = (
  isReportHours: boolean,
  arrayData: string,
  list: boolean,
  hideAudit: boolean
): string => {
  if (isReportHours) {
    return parseReportHoursData(arrayData);
  }

  if (list) {
    return parseArrayData(arrayData, true, true);
  }

  if (hideAudit) {
    return arrayData;
  }

  return parseArrayData(arrayData, false);
};

const resetCheckedInputs = (): void => {
  const inputs: any = document.querySelectorAll('.rf-checkbox__input');
  inputs.forEach((input) => {
    input.checked = false;
  });
};

const clearSelection = (
  value: string,
  dispatch: any,
  actionCreator: (value: string) => any
): void => {
  if (value !== '') {
    dispatch(actionCreator(''));
  }
};

const parseFilter = (filter) => {

  const elemInicio = 0;
  const elemFin = 2147483647;

  return {
    orden: filter.sortBy,
    ascDesc: filter.sortingSort,
    elemInicio,
    elemFin,
    elemFinal: elemFin, // este se repita porque control los filtros desde el componente
    // pero hay peticiones que cambian el nombre de este parámetro
    // envío los dos hasta que el API se estandarice
  };
};

const PaginationBlock = ({
  arrayDataIsEmpty,
  pages,
  handlePage,
  handleFilter,
  selectedPage,
  activePage,
  handlePaginationClick,
}: any): ReactElement | null => {
  if (!shouldShowPaginator(arrayDataIsEmpty, pages, handlePage)) {
    return null;
  }

  return (
    <div className="rf-paginator">
      {shouldShowStorePagination(handleFilter, pages) && (
        <RfUiPagination
          className={`adios ${selectedPage}, ${activePage}`}
          number-visible-pages={getVisiblePages()}
          number-of-pages={pages}
          active-page={selectedPage}
          page-selected={selectedPage}
          onPageSelected={handlePaginationClick}
        />
      )}

      {handleFilter && (
        <RfUiPagination
          className={`hola ${selectedPage}, ${activePage}`}
          number-visible-pages={getVisiblePages()}
          number-of-pages={handlePage}
          active-page={activePage}
          page-selected={activePage}
          onPageSelected={handlePaginationClick}
        />
      )}
    </div>
  );
};

const CardTableView = ({
  isReportHours,
  headers,
  literalsHeaderTable,
  arrayData,
  list,
  hideAudit,
  showDetailLinkResponsive,
  showSelectList,
  redirect,
  handleCheckPractica,
  handleCellSelect,
  handleSort,
  dataCount,
  handleCheckAll,
  itemsOnLeft,
  orderMobile,
  orderOptions,
  handleShoose,
}: any): ReactElement => (
  <div className="rf-container-card-table">
    <RfUiCardTable
      literals-header-table={getHeaderTable(
        isReportHours,
        headers,
        literalsHeaderTable
      )}
      literals-body-table={getBodyTable(
        isReportHours,
        arrayData,
        list,
        hideAudit
      )}
      extractLast={showDetailLinkResponsive}
      selectList={showSelectList}
      onNavigation={redirect}
      onCheckChange={(e: any) => {
        console.log('Cell Select');
        handleCheckPractica(e);
      }}
      onSelectChange={(e) => {
        handleCellSelect(e);
      }}
      onSelectOrderChange={handleSort}
      selectAllTitle={hasData(dataCount)}
      onCheckChangeAll={(e) => {
        console.log('Change ALL');
        handleCheckAll(e.detail.data);
      }}
      indexBreak={itemsOnLeft || 5}
      order={hasData(dataCount) ? orderMobile : false}
      optionsOrder={orderOptions}
      onShooseImg={(e) => handleShoose(e.detail.data)}
    />
  </div>
);

const CdkTableView = ({
  headers,
  literalsHeaderTable,
  arrayData,
  handleSort,
  redirect,
  handleCheckPractica,
  handleCheckAll,
  handleCellSelect,
  handleShoose,
}: any): ReactElement => (
  <div className="rf-container-table">
    <RfCdkTable
      literals-header-table={JSON.stringify(headers || literalsHeaderTable)}
      literals-body-table={arrayData}
      title-table="Listado de prácticas para certificados"
      onSorting={handleSort}
      onNavigation={redirect}
      onCheckChange={(e: any) => {
        handleCheckPractica(e);
      }}
      onCheckChangeAll={(e) => {
        handleCheckAll(e.detail.data);
      }}
      onSelectChange={(e) => {
        handleCellSelect(e.detail);
      }}
      onShooseImg={(e) => handleShoose(e.detail.data)}
    />
  </div>
);

const NoDataMessage = ({ arrayDataIsEmpty }: any): ReactElement | null => {
  if (!arrayDataIsEmpty) {
    return null;
  }

  return <p className="table-no-data-message">No hay resultados</p>;
};

const Table = ({
  redirect,
  list,
  showSelectList,
  showButtonDetail,
  data,
  dataCount,
  headers,
  handleFilter,
  hideAudit,
  handleCellSelect,
  handleShoose,
  handleCheckAll,
  handleCheckPractica,
  pagination25, // Element only from "Informe de horas" -> true if it is 25 elements for page default
  orderMobile,
  itemsOnLeft,
  orderOptions,
  isReportHours = false,
}: any): ReactElement => {
  const dispatch = useDispatch();
  const {
    practicesParse,
    practicasAuditoriaParse,
    practicasAuditoriaSeleccionadas,
    practicasReclamadaSeleccionadas,
    practicasRechazadaSeleccionadas,
    practicesValidSelect,
    profileActive,
    pages,
    selectedPage,
  } = useSelector<any, any>(selectors);

  const defPagination = getDefaultPagination(pagination25);
  const [handlePage, setHandlePage] = useState(0);
  const savedPageActiveNumber = getSessionNumber(
    `pageActive-${window.location.pathname}`,
    '1'
  );
  const [activePage, setActivePage] = useState(savedPageActiveNumber);
  const savedElemPage = getSessionNumber(
    `elementsPerPage-${window.location.pathname}`,
    defPagination
  );
  const [elemPage, setElemPage] = useState(savedElemPage);
  const [showDetailLinkResponsive] = useState(showButtonDetail);

  const literalsHeaderTable: LiteralHeaderTable[] = useMemo(
    () => (list ? [] : getHeaderAudit()),
    [list, profileActive]
  );

  useEffect(() => {
    const savedPageNumber = getSessionNumber(
      `pageSelected-${window.location.pathname}`,
      '1'
    );
    if (savedPageNumber !== selectedPage) {
      dispatch(setSelectedPage(savedPageNumber));
    }
    const savedPageResults = getSessionNumber(
      `elementsPerPage-${window.location.pathname}`,
      defPagination
    );
    if (savedPageResults !== elemPage) {
      setElemPage(savedPageResults);
    }
    const savedPageActiveNumber = getSessionNumber(
      `pageActive-${window.location.pathname}`,
      '0'
    );
    if (savedPageActiveNumber !== activePage) {
      setActivePage(savedPageActiveNumber);
    }
  }, [window.location.pathname]);

  useEffect(() => {
    dispatch(setElementsPerPage(elemPage));
  }, [elemPage]);

  const filter = useRef({
    page: 1,
    elementsPerPage: elemPage,
    sortBy: '',
    sortingSort: '',
  });

  const arrayData = useMemo(
    () => buildArrayData(data, list, practicesParse, practicasAuditoriaParse),
    [data, list, practicesParse, practicasAuditoriaParse]
  );

  const arrayDataIsEmpty = useMemo(
    () => isJsonArrayEmpty(arrayData),
    [arrayData]
  );

  const pagination: string = useMemo(
    () => buildPaginationOptions(pagination25),
    [pagination25, elemPage]
  );

  const calcNumberOfPages = () => {
    const savedPageActiveNumber = getSessionNumber(
      `pageActive-${window.location.pathname}`,
      '0'
    );
    const savedPages = getSessionNumber(
      `pages-${window.location.pathname}`,
      '0'
    );
    const pages = dataCount
      ? Math.ceil(dataCount / filter.current.elementsPerPage)
      : 0;

    if (pages !== Number(savedPages)) {
      if (pages) {
        setActivePage(savedPageActiveNumber !== 0 ? savedPageActiveNumber : 1);
      } else {
        setActivePage(0);
      }
      sessionStorage.setItem(
        `pages-${window.location.pathname}`,
        pages.toString()
      );
    }
    setHandlePage(pages);
  };

  const clearSelectedPractices = (): void => {
    resetCheckedInputs();
    clearSelection(
      practicasAuditoriaSeleccionadas,
      dispatch,
      setPracticaAuditoriaSeleccionada
    );
    clearSelection(
      practicasReclamadaSeleccionadas,
      dispatch,
      setPracticaReclamadaSeleccionada
    );
    clearSelection(
      practicasRechazadaSeleccionadas,
      dispatch,
      setPracticaRechazadaSeleccionada
    );
    clearSelection(practicesValidSelect, dispatch, setPracticaValidadaSeleccionada);
  };

  const handleResultsNumberChange = (e: any) => {
    paginationChangeNumber(window.location.pathname, true);
    const elementsPerPage = parseInt(e.detail);

    sessionStorage.setItem(
      `elementsPerPage-${window.location.pathname}`,
      e.detail
    );
    setElemPage(elementsPerPage);

    if (handleFilter) {
      filter.current = { ...filter.current, elementsPerPage, page: 1 };
      calcNumberOfPages();
      handleFilter(parseFilter(filter.current));
    }
  };

  const handlePaginationClick = (e: CustomEvent) => {
    clearSelectedPractices();
    const page = e.detail;
    sessionStorage.setItem(`pageSelected-${window.location.pathname}`, page);
    sessionStorage.setItem(`pageActive-${window.location.pathname}`, page);
    dispatch(setSelectedPage(page));

    if (handleFilter) {
      filter.current = { ...filter.current, elementsPerPage: elemPage, page };
      setActivePage(page);
      handleFilter(parseFilter(filter.current));
    }
  };

  const handleSort = (e: CustomEvent): void => {
    const sort = {
      sortBy: getSortLabel(e.detail.order),
      sortingSort: e.detail.ascDesc,
    } as Sort;
    dispatch(setSort(sort));

    if (handleFilter) {
      filter.current = { ...filter.current, ...sort };
      handleFilter(parseFilter(filter.current));
    }
  };

  useEffect(() => {
    calcNumberOfPages();
  }, [dataCount]);

  return (
    <>
      <div
        className={`rf-flex rf-justify-space rf-align-center
        ${isMobileOnly ? 'rf-block--mobile' : ''}`}
      >
        <RfUiSelectList
          className="card-table-result-content"
          label="Ver"
          second-label={`resultados por página de ${dataCount}`}
          options={pagination}
          // TODO - falta una propiedad en este elemento del ui-kit para seleccionar el valor
          onOptionActive={handleResultsNumberChange}
        />
      </div>

      <CardTableView
        isReportHours={isReportHours}
        headers={headers}
        literalsHeaderTable={literalsHeaderTable}
        arrayData={arrayData}
        list={list}
        hideAudit={hideAudit}
        showDetailLinkResponsive={showDetailLinkResponsive}
        showSelectList={showSelectList}
        redirect={redirect}
        handleCheckPractica={handleCheckPractica}
        handleCellSelect={handleCellSelect}
        handleSort={handleSort}
        dataCount={dataCount}
        handleCheckAll={handleCheckAll}
        itemsOnLeft={itemsOnLeft}
        orderMobile={orderMobile}
        orderOptions={orderOptions}
        handleShoose={handleShoose}
      />

      <CdkTableView
        headers={headers}
        literalsHeaderTable={literalsHeaderTable}
        arrayData={arrayData}
        handleSort={handleSort}
        redirect={redirect}
        handleCheckPractica={handleCheckPractica}
        handleCheckAll={handleCheckAll}
        handleCellSelect={handleCellSelect}
        handleShoose={handleShoose}
      />

      <NoDataMessage arrayDataIsEmpty={arrayDataIsEmpty} />

      <PaginationBlock
        arrayDataIsEmpty={arrayDataIsEmpty}
        pages={pages}
        handlePage={handlePage}
        handleFilter={handleFilter}
        selectedPage={selectedPage}
        activePage={activePage}
        handlePaginationClick={handlePaginationClick}
      />
    </>
  );
};

export default Table;
